import React, { useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { loadFile, SUPPORTED_FORMATS, getAllSupportedExtensions } from '../utils/loaders';

export default function FileImporter({ onFileLoad, onError }) {
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [svgOptions, setSvgOptions] = useState({
    svgDepth: 2,
    svgScale: 1
  });

  const handleFile = async (file) => {
    if (!file) return;

    setLoading(true);
    setFileName(file.name);

    try {
      const result = await loadFile(file, svgOptions);
      onFileLoad(result);
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const supportedExts = getAllSupportedExtensions();
  const acceptString = supportedExts.map(ext => `.${ext}`).join(',');

  return (
    <div className="panel">
      <div className="panel-header">Import</div>

      <div
        className={`drop-zone ${isDragging ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        {loading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="spinner"></div>
            <div className="text-sm text-neutral-400">Loading {fileName}...</div>
          </div>
        ) : (
          <>
            <UploadCloud size={30} className="mx-auto mb-3 text-sky-600" />
            <div className="text-sm font-medium text-neutral-200 mb-2">
              Drop or browse
            </div>
            <div className="text-xs text-neutral-500">
              Supported formats: {supportedExts.join(', ').toUpperCase()}
            </div>
          </>
        )}
        <input
          id="file-input"
          type="file"
          accept={acceptString}
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {fileName && !loading && (
        <div className="mt-3 text-xs text-neutral-400 truncate">
          Last loaded: {fileName}
        </div>
      )}

      <div className="divider"></div>

      <div className="space-y-3">
        <div className="text-sm font-medium text-neutral-200">SVG Extrusion</div>
        
        <div className="grid-2">
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Extrude Depth</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={svgOptions.svgDepth}
              onChange={(e) => setSvgOptions(prev => ({ ...prev, svgDepth: parseFloat(e.target.value) || 0 }))}
              className="input w-full"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Scale</label>
            <input
              type="number"
              step="0.1"
              min="0.01"
              value={svgOptions.svgScale}
              onChange={(e) => setSvgOptions(prev => ({ ...prev, svgScale: parseFloat(e.target.value) || 1 }))}
              className="input w-full"
            />
          </div>
        </div>
      </div>

      <div className="divider"></div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-neutral-200 mb-2">Supported Formats</div>
        {Object.values(SUPPORTED_FORMATS).map(format => (
          <div key={format.name} className="flex items-center justify-between text-xs">
            <span className="text-neutral-400">{format.name}</span>
            <span className="badge badge-info">
              {format.extensions.map(e => `.${e}`).join(', ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

